import { Trans } from '@lingui/react/macro';
import { Copy01 } from '@untitledui/icons/Copy01';
import { Mail01 } from '@untitledui/icons/Mail01';
import { useMemo, type ReactNode } from 'react';

import { ExternalLinkButton } from '@/components/base/buttons/button';
import {
  PROJECT_REFERRAL_MINIMUM_INCENTIVE,
  PROJECT_REFERRAL_REWARD,
} from '@/constants/config';
import { useClipboard } from '@/hooks/use-clipboard';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { cx } from '@/utils/cx';

import { Button } from '../base/buttons/button';
import { MessengerIcon } from '../foundations/messenger-icon';
import { WhatsAppIcon } from '../foundations/whatsapp-icon';

type ProjectReferralsCardVariant = 'card' | 'compact' | 'inline';

function CardContent() {
  return (
    <>
      <p className="mb-0 text-sm text-secondary">
        <Trans>
          Earn{' '}
          <span className="font-semibold text-primary">
            ${PROJECT_REFERRAL_REWARD}
          </span>{' '}
          when you refer a new Respondent user who completes a referred project
          and earns{' '}
          <span className="font-semibold text-primary">
            ${PROJECT_REFERRAL_MINIMUM_INCENTIVE}
          </span>{' '}
          in total incentive on a referred project.{' '}
        </Trans>
        <ExternalLinkButton
          href="https://help.respondent.io/en/articles/5464425-respondent-referral-policy-for-participants"
          target="_blank"
          color="link-color"
          className="display-inline"
        >
          <Trans>Learn more</Trans>
        </ExternalLinkButton>
      </p>
    </>
  );
}

function CardActionButtons({
  onCopyLink,
  copied,
  onEmailShare,
  onMessengerShare,
  onWhatsAppShare,
}: {
  onCopyLink: () => void;
  copied: boolean;
  onEmailShare: () => void;
  onMessengerShare: () => void;
  onWhatsAppShare: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        color="secondary"
        size="md"
        iconLeading={Copy01}
        onClick={onCopyLink}
      >
        {copied ? <Trans>Copied!</Trans> : <Trans>Copy link</Trans>}
      </Button>

      <Button
        color="secondary"
        size="md"
        iconLeading={Mail01}
        onClick={onEmailShare}
        aria-label="Share via email"
      />

      <Button
        color="secondary"
        size="md"
        onClick={onMessengerShare}
        aria-label="Share via Messenger"
        iconLeading={MessengerIcon}
      ></Button>

      <Button
        color="secondary"
        size="md"
        onClick={onWhatsAppShare}
        aria-label="Share via WhatsApp"
        iconLeading={WhatsAppIcon}
      ></Button>
    </div>
  );
}

function CompactVariant({ actionButtons }: { actionButtons: ReactNode }) {
  return (
    <>
      <div className="flex flex-col items-start gap-3">
        <div className="flex flex-col items-start gap-2">
          <h2 className="text-lg font-bold text-primary">
            {'🎁 '}
            <Trans>Share this project</Trans>
          </h2>
          <CardContent />
        </div>
        {actionButtons}
      </div>
    </>
  );
}

function CardVariant({ actionButtons }: { actionButtons: ReactNode }) {
  return (
    <div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-row items-center gap-6">
          <span className="text-5xl leading-none">🎁</span>
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-bold text-primary">
              <Trans>Share this project</Trans>
            </h2>
            <CardContent />
          </div>
        </div>
        <div className="pl-18">{actionButtons}</div>
      </div>
    </div>
  );
}

export function ProjectSpecificReferralsCard({
  variant = 'card',
  referralLink,
  messengerShareLink,
  className,
  backgroundImage,
  projectName,
  incentiveAmount,
}: {
  variant?: ProjectReferralsCardVariant;
  referralLink: string;
  messengerShareLink: string;
  className?: string;
  backgroundImage?: string;
  projectName: string;
  incentiveAmount: number;
}) {
  const { copy, copied } = useClipboard();
  const analytics = useAnalytics();

  const body = encodeURIComponent(
    `Hey!

I recently signed up to Respondent where you get paid for participating in research projects.

I thought you might be a good fit for a research project that is titled: ${projectName}

You'll get paid $${incentiveAmount} for participating!

Use this link to see more information and apply: ${referralLink}
        `,
  );

  const handleCopyLink = () => {
    analytics?.ui({
      action: 'clicked',
      actionSubject: 'referralCopyLink',
      source: 'referralCard',
      attributes: { channel: 'copy_link' },
    });
    void copy(referralLink);
  };

  const handleEmailShare = () => {
    analytics?.ui({
      action: 'clicked',
      actionSubject: 'referralShare',
      actionSubjectId: 'email',
      source: 'referralCard',
      attributes: { channel: 'email' },
    });
    const subject = encodeURIComponent(`${projectName} - $${incentiveAmount}`);

    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleMessengerShare = () => {
    if (messengerShareLink) {
      analytics?.ui({
        action: 'clicked',
        actionSubject: 'referralShare',
        actionSubjectId: 'messenger',
        source: 'referralCard',
        attributes: { channel: 'messenger' },
      });
      window.open(messengerShareLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleWhatsAppShare = () => {
    analytics?.ui({
      action: 'clicked',
      actionSubject: 'referralShare',
      actionSubjectId: 'whatsapp',
      source: 'referralCard',
      attributes: { channel: 'whatsapp' },
    });
    window.open(`https://wa.me/?text=${body}`, '_blank', 'noopener,noreferrer');
  };

  const actionButtons = (
    <CardActionButtons
      onCopyLink={handleCopyLink}
      copied={!!copied}
      onEmailShare={handleEmailShare}
      onMessengerShare={handleMessengerShare}
      onWhatsAppShare={handleWhatsAppShare}
    />
  );

  const backgroundStyle = useMemo(
    () =>
      backgroundImage
        ? {
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover' as const,
            backgroundPosition: 'center' as const,
          }
        : undefined,
    [backgroundImage],
  );

  return (
    <div
      className={cx(
        className,
        'h-full rounded-3xl p-5 md:px-6',
        !backgroundImage && 'bg-utility-pink-50',
      )}
      style={backgroundStyle}
    >
      {variant === 'card' && <CardVariant actionButtons={actionButtons} />}
      {variant === 'compact' && (
        <CompactVariant actionButtons={actionButtons} />
      )}
    </div>
  );
}
